import type { SortingState, VisibilityState } from "@tanstack/react-table";

/**
 * Combined (AND) filter set for the task table. Empty arrays / empty assignee
 * mean "no constraint on this dimension".
 */
export type TaskFilters = {
  status: string[];
  type: string[];
  priority: number[];
  assignee: string; // "" = any, "__none__" = unassigned, else a userId
};

export const EMPTY_FILTERS: TaskFilters = {
  status: [],
  type: [],
  priority: [],
  assignee: "",
};

/** Everything needed to reconstruct a table layout: columns, sort, filters. */
export type TableViewState = {
  sorting: SortingState;
  columnVisibility: VisibilityState;
  columnOrder: string[];
  filters: TaskFilters;
};

export type SavedView = { name: string; state: TableViewState };

const keyFor = (projectId: string) => `task-table-views:${projectId}`;

export function loadSavedViews(projectId: string): SavedView[] {
  try {
    const raw = localStorage.getItem(keyFor(projectId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as SavedView[]) : [];
  } catch {
    return [];
  }
}

export function persistSavedViews(projectId: string, views: SavedView[]): void {
  try {
    localStorage.setItem(keyFor(projectId), JSON.stringify(views));
  } catch {
    /* best-effort — private mode / quota */
  }
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

function csvCell(value: unknown): string {
  const s = value == null ? "" : String(value);
  // Quote always; double embedded quotes. Excel/Sheets parse this reliably.
  return `"${s.replace(/"/g, '""')}"`;
}

export function toCsv(headers: string[], rows: string[][]): string {
  return [headers, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n");
}

export function downloadCsv(filename: string, csv: string): void {
  // Prepend a BOM so Excel opens UTF-8 correctly.
  const blob = new Blob(["﻿" + csv], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
